import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Star, MapPin, Check, ArrowLeft, Heart, Share2, ChevronLeft, ChevronRight } from 'lucide-react';
import { getVehicleById, getVehicleReviews } from '../api';

const VehicleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const [checkIn, setCheckIn] = useState();
  const [checkOut, setCheckOut] = useState();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [vehicleData, reviewsData] = await Promise.all([
          getVehicleById(id),
          getVehicleReviews(id)
        ]);
        setVehicle(vehicleData);
        setReviews(reviewsData);
      } catch (error) {
        console.error('Error fetching vehicle details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const totalDays = useMemo(() => {
    if (checkIn && checkOut) {
      const diffTime = Math.abs(checkOut - checkIn);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays || 1;
    }
    return 1;
  }, [checkIn, checkOut]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500">Loading vehicle details...</p>
        </div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-black mb-2">Vehicle not found</h2>
          <Button onClick={() => navigate('/search')} className="bg-red-600 hover:bg-red-700 text-white">
            Back to Search
          </Button>
        </div>
      </div>
    );
  }

  const images = vehicle.images || [vehicle.image];
  const totalPrice = vehicle.price * totalDays;
  const serviceFee = Math.round(totalPrice * 0.1);
  const grandTotal = totalPrice + serviceFee;

  const handleBooking = () => {
    navigate('/booking', { 
      state: { 
        vehicle,
        serviceType: 'vehicle_rental'
      } 
    });
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const disabledDates = (date) => {
    return date < new Date(new Date().setHours(0, 0, 0, 0));
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-black hover:text-red-600 transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Image Gallery */}
        <div className="relative mb-8 rounded-2xl overflow-hidden">
          <div className="relative h-[500px]">
            <img
              src={images[currentImageIndex]}
              alt={`${vehicle.name} ${currentImageIndex + 1}`}
              className="w-full h-full object-cover"
            />
            {images.length > 1 && (
              <>
                <Button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/90 hover:bg-white text-black"
                >
                  <ChevronLeft className="h-6 w-6" />
                </Button>
                <Button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/90 hover:bg-white text-black"
                >
                  <ChevronRight className="h-6 w-6" />
                </Button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {images.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`h-2 rounded-full transition-all ${
                        idx === currentImageIndex ? 'w-8 bg-white' : 'w-2 bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
          <div className="absolute top-4 right-4 flex gap-2">
            <Button
              onClick={() => setIsFavorite(!isFavorite)}
              className="h-12 w-12 rounded-full bg-white/90 hover:bg-white"
            >
              <Heart className={`h-5 w-5 ${isFavorite ? 'fill-red-600 text-red-600' : 'text-black'}`} />
            </Button>
            <Button className="h-12 w-12 rounded-full bg-white/90 hover:bg-white">
              <Share2 className="h-5 w-5 text-black" />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Details */}
          <div className="lg:col-span-2">
            {/* Title and Rating */}
            <div className="mb-6">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h1 className="text-4xl font-bold text-black mb-2">{vehicle.name}</h1>
                  <p className="text-lg text-gray-600">{vehicle.category}</p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-red-600">${vehicle.price}</div>
                  <div className="text-sm text-gray-500">per day</div>
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center">
                  <Star className="h-5 w-5 fill-yellow-400 text-yellow-400 mr-1" />
                  <span className="font-semibold text-black">{vehicle.rating}</span>
                  <span className="text-gray-500 ml-1">({vehicle.reviewCount} reviews)</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <MapPin className="h-5 w-5 mr-1" />
                  <span>{vehicle.location}</span>
                </div>
              </div>
            </div>

            {/* Description */}
            <Card className="p-6 mb-6 border-gray-200">
              <h2 className="text-2xl font-bold text-black mb-4">About this vehicle</h2>
              <p className="text-gray-700 leading-relaxed">{vehicle.description}</p>
            </Card>

            {/* Features */}
            <Card className="p-6 mb-6 border-gray-200">
              <h2 className="text-2xl font-bold text-black mb-4">Features & Amenities</h2>
              <div className="grid grid-cols-2 gap-4">
                {vehicle.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center">
                    <div className="h-8 w-8 rounded-full bg-red-50 flex items-center justify-center mr-3">
                      <Check className="h-5 w-5 text-red-600" />
                    </div>
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Reviews */}
            <Card className="p-6 border-gray-200">
              <h2 className="text-2xl font-bold text-black mb-6">Guest Reviews</h2>
              <div className="space-y-6">
                {reviews.length > 0 ? (
                  reviews.map((review) => (
                    <div key={review.id} className="border-b border-gray-200 last:border-0 pb-6 last:pb-0">
                      <div className="flex items-center gap-3 mb-3">
                        <img
                          src={review.userImage}
                          alt={review.userName}
                          className="h-12 w-12 rounded-full object-cover"
                        />
                        <div>
                          <p className="font-semibold text-black">{review.userName}</p>
                          <p className="text-sm text-gray-500">{new Date(review.date).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div className="flex items-center mb-2">
                        {[...Array(review.rating)].map((_, i) => (
                          <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                        ))}
                      </div>
                      <p className="text-gray-700">{review.comment}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500">No reviews yet. Be the first to review!</p>
                )}
              </div>
            </Card>
          </div>

          {/* Right Column - Booking Card */}
          <div className="lg:col-span-1">
            <Card className="p-6 sticky top-24 border-gray-200 shadow-xl">
              <h3 className="text-2xl font-bold text-black mb-6">Reserve This Vehicle</h3>
              
              <div className="mb-6">
                <div className="text-center mb-4">
                  <div className="text-4xl font-bold text-red-600">${vehicle.price}</div>
                  <div className="text-sm text-gray-500">per day</div>
                </div>
              </div>

              <Button 
                onClick={handleBooking}
                className="w-full h-14 bg-red-600 hover:bg-red-700 text-white font-semibold text-lg transition-all hover:shadow-lg mb-4"
              >
                Book Now
              </Button>
              
              <div className="text-center">
                <p className="text-xs text-gray-500">
                  Flexible booking options available
                </p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleDetail;
